import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { mainButton, miniApp } from '@telegram-apps/sdk-react';

import { useBackButtonToPrevRoute } from '@/hooks/useBackButtonToPrevRoute.tsx';
import Edit from '@/components/ProfileData/Edit/Edit.tsx';
import {
  allTabsBlackBackground,
  previewTab,
  selectedTab,
  tab,
  tabsContainer,
  verticalSeparator
} from '@/pages/Profile/Edit/styles.ts';
import { CandidateUI } from '@/api/matchmaker/types.ts';
import { useProfileStore } from '@/store/useProfileStore.ts';
import { PhotoKind, UserDataFields } from '@/shared/type/userDataFields.ts';
import CardPreview from '@/components/Discovery/Card/CardPreview.tsx';
import UIConfig from '@/UIConfig.ts';
import {
  AlcoholPreference,
  BodyType,
  EmploymentSector,
  Gender,
  PrivacyLabels,
  SmokingStatus,
  UserLabels,
  UserStates,
  ZodiacSign
} from '@/enums/apiEnums.ts';
import { routes } from '@/navigation/routes.tsx';
import { useHeaderStore } from '@/store/useHeadersStylesStore.ts';
import { helpers } from '@/helpers.ts';

enum TabsEnum {
  PREVIEW = 'PREVIEW',
  EDIT = 'EDIT'
}

const EditProfile: FC = () => {
  useBackButtonToPrevRoute(routes.profile);
  const { aboutMe } = useProfileStore();
  const { setHeaderColors } = useHeaderStore();

  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState<TabsEnum>(TabsEnum.EDIT);
  const [toggleFirstPhoto, setToggleFirstPhoto] = useState(true);

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const isPreviewTabActive = activeTab === TabsEnum.PREVIEW;

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, []);

  useEffect(() => {
    if (activeTab === TabsEnum.PREVIEW) {
      miniApp.setHeaderColor('#1D1D1D');
      setHeaderColors({
        backgroundColor: UIConfig.Colors.BackgroundDark,
        logoColor: UIConfig.Colors.BackgroundWhite
      });
      mainButton.setParams({ isVisible: false });
    } else {
      miniApp.setHeaderColor('#FFFFFF');
      setHeaderColors({
        backgroundColor: UIConfig.Colors.BackgroundWhite,
        logoColor: UIConfig.Colors.BackgroundDark
      });
      mainButton.setParams({
        backgroundColor: '#000000',
        text: t('button.save')
      });
    }
  }, [activeTab]);

  const handleTabChange = (tabKey: TabsEnum) => {
    setActiveTab(tabKey);

    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: 0
      });
    }
  };

  const renderTabButton = useCallback(
    (tabKey: TabsEnum, label: string) => {
      const tabStyles = {
        ...tab,
        ...(activeTab === TabsEnum.PREVIEW
          ? allTabsBlackBackground
          : { backgroundColor: UIConfig.Colors.BackgroundWhite }),
        ...(activeTab === tabKey ? selectedTab : {}),
        ...(tabKey === TabsEnum.PREVIEW && activeTab === TabsEnum.PREVIEW
          ? previewTab
          : {})
      };

      return (
        <div style={tabStyles} onClick={() => handleTabChange(tabKey)}>
          {label}
        </div>
      );
    },
    [activeTab]
  );

  const [userDataFields, setUserDataFields] = useState<UserDataFields>({
    name: aboutMe?.profile.name ?? '',
    about_me: aboutMe?.profile.about_me ?? '',
    gender: aboutMe?.profile.gender as Gender,
    birthday: aboutMe?.profile.birthday ?? '',
    age: aboutMe?.profile.age ?? 0,
    photos:
      aboutMe?.profile.v2photos.map((photo) => ({
        file: undefined,
        hid: photo.hid,
        url: {
          [PhotoKind.KIND_PRIMARY]:
            helpers.getPrimaryPhoto(photo.variants) ?? '',
          [PhotoKind.KIND_THUMB]: helpers.getThumbPhoto(photo.variants) ?? ''
        },
        state: photo.state,
        thumbhash: helpers.getThumbHash(photo.variants)
      })) ?? [],
    photosOrigin: []
  });

  const updateFields = useCallback((fields: Partial<UserDataFields>) => {
    setUserDataFields((prev) => ({ ...prev, ...fields }));
  }, []);

  const selfPreview: CandidateUI = useMemo(() => {
    const isDistanceHidden = aboutMe?.profile.concealed_attributes.includes(
      PrivacyLabels.CONCEALABLE_ATTRIBUTE_LOCATION
    );

    return {
      isActive: true,
      isJustJoined: true,
      likesMe: false,
      activityLabels: [
        UserStates.ONLINE_STATE_ONLINE,
        UserLabels.LABEL_JUST_JOINED
      ],
      profile: {
        aboutMe: userDataFields.about_me,
        age: userDataFields.age,
        cityName: aboutMe?.profile?.city?.name ?? '',
        gender: aboutMe?.profile?.gender,
        bodyType: aboutMe?.profile.body_type ?? BodyType.BODY_TYPE_UNSPECIFIED,
        smokingStatus:
          aboutMe?.profile.smoking_status ??
          SmokingStatus.SMOKING_STATUS_UNSPECIFIED,
        alcoholPreference:
          aboutMe?.profile.alcohol_preference ??
          AlcoholPreference.ALCOHOL_PREFERENCE_UNSPECIFIED,
        employmentSector:
          aboutMe?.profile.employment_sector ??
          EmploymentSector.EMPLOYMENT_SECTOR_UNSPECIFIED,
        zodiacSign:
          aboutMe?.profile.zodiac_sign ?? ZodiacSign.ZODIAC_SIGN_UNSPECIFIED,
        geoProximity: isDistanceHidden ? 0 : 0.1,
        heightCm: aboutMe?.profile.height_cm ?? 0,
        hid: aboutMe?.profile?.hid ?? '',
        name: userDataFields.name,
        v2photos:
          userDataFields.photos.map((el) => ({
            variants: Object.entries(el.url).map(([kind, url]) => ({
              url: url as string,
              kind: kind as PhotoKind,
              thumbhash: el.thumbhash ?? ''
            }))
          })) ?? [],
        v2videos: aboutMe?.profile?.v2videos?.map((video) => ({
          variants: video.variants.map((v) => ({
            url: v.url,
            kind: v.kind
          }))
        }))
      }
    };
  }, [userDataFields, aboutMe]);

  const tabsConfig = [
    {
      key: TabsEnum.EDIT,
      component: (
        <Edit userDataFields={userDataFields} updateFields={updateFields} />
      )
    },
    {
      key: TabsEnum.PREVIEW,
      component: (
        <CardPreview data={selfPreview} toggleFirstPhoto={toggleFirstPhoto} />
      )
    }
  ];

  useEffect(() => {
    setToggleFirstPhoto((prev) => !prev);
  }, [activeTab]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 100px)',
        paddingTop: '100px'
      }}
    >
      <div
        style={{
          ...tabsContainer,
          ...(isPreviewTabActive ? { borderBottom: '1px solid #303030' } : {})
        }}
      >
        {renderTabButton(TabsEnum.EDIT, t('profile.edit_tab'))}
        <div
          style={{
            ...verticalSeparator,
            ...(isPreviewTabActive ? { backgroundColor: '#303030' } : {})
          }}
        />
        {renderTabButton(TabsEnum.PREVIEW, t('profile.preview_tab'))}
      </div>

      <div
        ref={scrollContainerRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          overscrollBehavior: 'none'
        }}
      >
        {tabsConfig.map(({ key, component }) => (
          <div
            key={key}
            style={{ display: activeTab === key ? 'block' : 'none' }}
          >
            {component}
          </div>
        ))}
      </div>
    </div>
  );
};

export default EditProfile;
